import mmLogoStacked from '@/assets/logos/mm-logo_stack.svg';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Toast } from 'primereact/toast';

interface OidcProvider {
  id: string;
}

// Read available OIDC providers from environment variable (injected at build time)
// No fallback - if not configured, shows error toast and blank page
const OIDC_PROVIDERS: OidcProvider[] = (
  import.meta.env.VITE_OIDC_PROVIDERS || ''
)
  .split(',')
  .map((id: string) => ({ id: id.trim() }))
  .filter((p: { id: string }) => p.id.length > 0);

const ProviderSelectionPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const toast = useRef<Toast>(null);
  const [returnUrl, setReturnUrl] = useState('/');
  const hasProviders = OIDC_PROVIDERS.length > 0;

  useEffect(() => {
    // Set page title for accessibility
    document.title = t('auth.selectProvider.pageTitle');
  }, [t]);

  useEffect(() => {
    // Show error toast if no providers are configured
    if (!hasProviders) {
      toast.current?.show({
        severity: 'error',
        summary: t('auth.selectProvider.noProvidersTitle'),
        detail: t('auth.selectProvider.noProvidersMessage'),
        sticky: true,
      });
    }
  }, [hasProviders, t]);

  useEffect(() => {
    // Extract returnUrl from query params or use default
    const params = new URLSearchParams(location.search);
    const url = params.get('returnUrl') || '/';
    setReturnUrl(url);
  }, [location.search]);

  const handleProviderLogin = (providerId: string) => {
    // Redirect to OIDC login with provider parameter
    const loginUrl = `/api/auth/oidc/login?returnUrl=${encodeURIComponent(returnUrl)}&provider=${providerId}`;
    window.location.href = loginUrl;
  };

  const getProviderLabel = (providerId: string): string => {
    return t(`auth.providers.${providerId}`, providerId);
  };

  // Show blank page with toast when no providers configured
  if (!hasProviders) {
    return (
      <main
        className='flex flex-col items-center justify-center min-h-screen p-4 bg-surface-ground'
        role='main'
      >
        <Toast ref={toast} />
      </main>
    );
  }

  return (
    <main
      className='flex flex-col items-center justify-center min-h-screen p-4 bg-surface-ground'
      role='main'
      aria-labelledby='provider-selection-title'
    >
      <Toast ref={toast} />
      <Card className='w-full max-w-md'>
        <div className='flex flex-col gap-4'>
          <header className='text-center mb-2'>
            <img
              src={mmLogoStacked}
              alt={t('layout.appName')}
              className='h-24 mx-auto mb-4'
            />
            <h1
              id='provider-selection-title'
              className='text-2xl font-semibold mb-2'
            >
              {t('auth.selectProvider.title')}
            </h1>
            <p className='text-color-secondary'>
              {t('auth.selectProvider.subtitle')}
            </p>
          </header>

          <nav
            className='flex flex-col gap-3 mt-4'
            role='navigation'
            aria-label={t('auth.selectProvider.title')}
          >
            {OIDC_PROVIDERS.map(provider => {
              const providerLabel = getProviderLabel(provider.id);
              return (
                <Button
                  key={provider.id}
                  label={providerLabel}
                  icon='pi pi-sign-in'
                  size='large'
                  className='w-full'
                  onClick={() => handleProviderLogin(provider.id)}
                  aria-label={t('auth.selectProvider.loginWith', {
                    provider: providerLabel,
                  })}
                />
              );
            })}
          </nav>
        </div>
      </Card>
    </main>
  );
};

export default ProviderSelectionPage;
